import { useState } from 'react';
import type { HTMLAttributes } from 'react';
import { useTranslations } from 'next-intl';
import Search from '@engineering/icons/search';
import {
  Ui,
  Button,
  Theme,
  useRawFeatureTranslations,
} from '@silvertours/front-shared';
import {
  Runtime,
  analytics,
  DateRangePicker,
} from '@silvertours/front-entities';
import { SearchFormOptions } from './SearchFormOptions';
import { Logo } from './Logo';
import { useSearchContext } from './context';
import { useSearchFormSubmit } from './hooks/useSearchFormSubmit';
import {
  ButtonWrapper,
  CardHeadline,
  DateErrorMessage,
  DepartureInput,
  DestinationInput,
  FooterLink,
  FormContainer,
  LocationErrorMessage,
  OptionsCloseIcon,
  OptionsOpenIcon,
  StyledBadge,
  StyledForm,
  ThinSecondRow,
  ThinToggleWrapper,
  ThinToggleArrowDown,
  ThinToggleArrowUp,
  Title,
  Wrapper,
  TryAgainButtonWrapper,
  PageHeadline,
  Subtitle,
  ThinToggleButton,
} from './SearchForm.styles';
import { OneWayToggle } from './OneWayToggle';
import { AgeInput } from './AgeInput';
import { AgeDropdown } from './AgeDropdown';
import { isTransporterSearch } from './utils';

type SearchFormProps = {
  customColor?: string;
  stageTitle?: string;
  isWide?: boolean;
  isAffiliate?: boolean;
  isErrorPage?: boolean;
  isPromoPage?: boolean;
  showPoweredBy?: boolean;
  headline?: string;
  displayMode?: 'full' | 'thin';
  isStageVisibility?: boolean;
  isModernStage?: boolean;
  /**
   * `card` is the stand-alone card of the index page variant. It shows the
   * headline as an H2 and has no cancellation badge. Use it with `isWide`.
   */
  layout?: 'default' | 'card';
} & Pick<HTMLAttributes<HTMLFormElement>, 'className'>;

export const SearchForm = ({
  customColor,
  headline,
  isWide,
  isAffiliate,
  isErrorPage,
  isPromoPage,
  stageTitle,
  showPoweredBy,
  displayMode = 'full',
  isStageVisibility,
  isModernStage,
  className,
  layout = 'default',
}: SearchFormProps) => {
  const {
    setCustomerAge,
    setDate,
    setError,
    setInputValue,
    setShowDestLocation,
    state,
    isInternalCall,
    prefilterQuery,
  } = useSearchContext();

  const t = useTranslations('features.stageLegacy.stage.searchForm');
  const isMobileTouchDevice = Theme.useIsMobileTouchDevice();
  const { market } = Runtime.useLocale();
  const isSwissPage = market === 'CH';
  // `textCH` only exists in the German catalogue, so it cannot be addressed as
  // a message key — reading the group keeps it typed as `string | undefined`.
  const { tooltip } = useRawFeatureTranslations(
    'stageLegacy.stage.searchForm.cancellation',
  );
  const cancellationTooltipText =
    isSwissPage && tooltip.textCH ? tooltip.textCH : tooltip.text;

  const {
    loading,
    checkedOptions,
    handleOptionsChange,
    handleSearchSubmit,
    handleSubmit,
  } = useSearchFormSubmit();

  const [showOptions, setShowOptions] = useState(false);
  const [shouldSuppressDateError, setShouldSuppressDateError] = useState(false);
  const [isThinExpanded, setIsThinExpanded] = useState(false);

  const toggleOptions = () => {
    const showOptionsValue = !showOptions;
    setShowOptions(showOptionsValue);
    analytics.gtm.trackToggleMoreOptions(showOptionsValue);
  };

  if (!state) {
    return null;
  }

  const toggleDestLocation = (forceToggle = false) => {
    analytics.generic.trackReturnOnDifferentLocationClick(forceToggle);
    setError({ kind: 'locationError', message: '' });

    if (isWide) {
      setInputValue('destination', '');
    }

    if (!isWide || forceToggle) {
      setShowDestLocation(!state.showDestLocation);
    }
  };

  const isThin = displayMode === 'thin';
  const isCard = layout === 'card';
  const isCollapsed = isThin && !isThinExpanded;

  const title = stageTitle || t('title');
  // in thin mode, destination input is controlled only by the checkbox, not collapse state
  const showDestinationInput = !!state.showDestLocation;
  // in thin mode, second row (toggle + age) is hidden when collapsed
  const showOneWayToggle = isThin
    ? !isCollapsed
    : !state.showDestLocation || !!isWide;
  const showAgeInput = isThin ? !isCollapsed : true;
  const showOptionsToggle =
    !isAffiliate && !isThin && ((isWide && isInternalCall) || !isWide);
  const showLogo = !!isAffiliate && !!showPoweredBy && !isMobileTouchDevice;

  const hasLocationError = !!state.locationError;

  const getLocationError = () => {
    if (showDestinationInput) {
      if (!state.depLocation && !state.destLocation) {
        return t('errorTexts.locationBoth');
      }
      if (!state.destLocation) {
        return t('errorTexts.dest');
      }
    }
    return t('errorTexts.dep');
  };

  return (
    <Wrapper
      aria-labelledby="search-form-title"
      className={className}
      $isWide={isWide}
      $isThin={isThin}
      $isStageVisibility={isStageVisibility}
      $isModernStage={isModernStage}
      $isCard={isCard}
    >
      <Title id="search-form-title" $isWide={isWide} $customColor={customColor}>
        {title}
      </Title>

      <StyledForm
        onSubmit={handleSubmit}
        id="search"
        role="search"
        aria-label={t('formTitle')}
        $isWide={isWide}
        $isThin={isThin}
        $isErrorPage={isErrorPage}
        $isModernStage={isModernStage}
        $isCard={isCard}
      >
        {isCard && !!headline && <CardHeadline>{headline}</CardHeadline>}
        {!isAffiliate &&
          !isCard &&
          (isErrorPage || isPromoPage ? (
            <Subtitle $isWide={isWide}>{t('subtitle')}</Subtitle>
          ) : (
            !!headline && <PageHeadline>{headline}</PageHeadline>
          ))}
        {!isThin && !isModernStage && !isCard && (
          <StyledBadge
            badgeText={t('cancellationBadge')}
            tooltipTitle={t('cancellationBadge')}
            tooltipText={cancellationTooltipText}
            tooltipLabel={t('cancellation.tooltip.label')}
            onHover={() => {
              analytics.gtm.trackCancellationHover();
              analytics.generic.trackCancellationHover();
            }}
            $isWide={isWide}
          />
        )}
        <FormContainer
          // Clarity Balanced Masking masks all inputs/dropdowns/numbers by default;
          // unmasking here (LPOT-1716) intentionally covers location, date/time and
          // driver's age for analytics — do not add this to shared Ui.Input/Ui.Dropdown,
          // since login and newsletter fields reuse those and must stay masked.
          data-clarity-unmask="true"
          $isWide={isWide}
          $isThin={isThin}
          $isCard={isCard}
          $hasDestinationInput={showDestinationInput}
          $hasMoreOptionsToggle={showOptionsToggle}
          $hasLocationError={hasLocationError}
          $hasDateError={!shouldSuppressDateError && !!state.dateError}
          $hasSecondRow={isThin && !isCollapsed}
        >
          <DepartureInput
            id="dep"
            onServiceNumbersSubmit={handleSearchSubmit}
            direction="departure"
            label={t('departure.label')}
            clearButtonLabel={t('departure.clearButtonLabel')}
            placeholder={t('departure.placeholder')}
            icon={isCard ? undefined : <Search />}
            ariaErrorMessage="location-error"
            cleanable
            unmaskForClarity
          />
          {!isThin && showOneWayToggle && (
            <OneWayToggle
              type={isWide ? 'toggle' : 'checkbox'}
              label={t('destination.toggleLabel')}
              id="destSelect"
              checked={showDestinationInput}
              onChange={() => toggleDestLocation(true)}
            />
          )}
          {showDestinationInput && (
            <DestinationInput
              id="dest"
              direction="destination"
              label={t('destination.label')}
              clearButtonLabel={t('destination.clearButtonLabel')}
              placeholder={t('destination.placeholder')}
              icon={isCard ? undefined : <Search />}
              ariaErrorMessage="location-error"
              cleanable={() => toggleDestLocation()}
              unmaskForClarity
            />
          )}
          {hasLocationError && (
            <LocationErrorMessage id="location-error" $isWide={isWide}>
              {getLocationError()}
            </LocationErrorMessage>
          )}
          <DateRangePicker
            from={new Date(state.depDate)}
            to={new Date(state.destDate)}
            invalid={!!state.dateError && !shouldSuppressDateError}
            onCalendarVisibilityChange={setShouldSuppressDateError}
            onDatesChange={dates => {
              setDate('departure', dates.from!);
              setDate('destination', dates.to!);
            }}
            vertical={isMobileTouchDevice}
            dateDisplay={isWide ? 'short' : 'default'}
            unmaskForClarity
          />
          {state.dateError && !shouldSuppressDateError && (
            <DateErrorMessage id="date-error" $isWide={isWide}>
              {state.dateError}
            </DateErrorMessage>
          )}
          {!isThin &&
            showAgeInput &&
            (isWide ? (
              <AgeInput value={state.customerAge} onChange={setCustomerAge} />
            ) : (
              <AgeDropdown
                value={state.customerAge}
                onChange={setCustomerAge}
                label={t('age.label')}
              />
            ))}
          <ButtonWrapper $customColor={customColor} $isThin={isThin}>
            <Button
              id="find"
              loading={loading}
              aria-label={loading ? t('button.loadingLabel') : undefined}
              type="submit"
              fullWidth
              icon={<Search width="24" height="24" />}
              textId="find-button-text"
            >
              {t(
                isTransporterSearch(prefilterQuery)
                  ? 'button.transporterLabel'
                  : 'button.label',
              )}
            </Button>
          </ButtonWrapper>
          {state.axiosError && !isWide && (
            <Ui.ErrorMessage>{t('errorTexts.request')}</Ui.ErrorMessage>
          )}
          {state.axiosError && isWide && (
            <Ui.Overlay
              open
              title={t('errorTexts.requestTitle')}
              titlePosition="left"
              width="wide"
              onClose={() => setError({ kind: 'axiosError', message: '' })}
            >
              <p>{t('errorTexts.request')}</p>
              <TryAgainButtonWrapper>
                <Button
                  id="try-again"
                  textId="try-again-text"
                  onClick={() => {
                    setError({ kind: 'axiosError', message: '' });
                    handleSearchSubmit();
                  }}
                >
                  {t('tryAgainButton.label')}
                </Button>
              </TryAgainButtonWrapper>
            </Ui.Overlay>
          )}
          {showLogo && <Logo />}
          {showOptionsToggle && (
            <FooterLink iconOnLeft={showOptions}>
              <Ui.TextLink
                id="more-options-text"
                as="button"
                onClick={toggleOptions}
              >
                {isWide ? t('options.internal') : t('options.label')}{' '}
                {checkedOptions.length > 0 && `(${checkedOptions.length})`}
                {showOptions ? (
                  <OptionsOpenIcon id="more-options-open-icon" />
                ) : (
                  <OptionsCloseIcon id="more-options-close-icon" />
                )}
              </Ui.TextLink>
            </FooterLink>
          )}
          {isThin && (
            <ThinToggleWrapper>
              <ThinToggleButton
                type="button"
                onClick={() => setIsThinExpanded(prev => !prev)}
              >
                {isThinExpanded ? t('thinForm.less') : t('thinForm.more')}
                {isThinExpanded ? (
                  <ThinToggleArrowUp id="thin-expand-open-icon" />
                ) : (
                  <ThinToggleArrowDown id="thin-expand-close-icon" />
                )}
              </ThinToggleButton>
            </ThinToggleWrapper>
          )}
          {/* thin mode second row — flex container, visible only when expanded */}
          {isThin && (showOneWayToggle || showAgeInput) && (
            <ThinSecondRow>
              {showOneWayToggle && (
                <OneWayToggle
                  type="toggle"
                  label={t('destination.toggleLabel')}
                  id="destSelect"
                  checked={showDestinationInput}
                  onChange={() => toggleDestLocation(true)}
                />
              )}
              {showAgeInput && (
                <AgeInput value={state.customerAge} onChange={setCustomerAge} />
              )}
            </ThinSecondRow>
          )}
        </FormContainer>
        {showOptions && (
          <SearchFormOptions
            onChange={handleOptionsChange}
            setShowOptions={setShowOptions}
            checked={checkedOptions}
            forceOverlay={!!isWide}
          />
        )}
      </StyledForm>
    </Wrapper>
  );
};
